import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCountries, setCountriesLoading, setCountriesError } from "../redux/actions/dashboard";
import { v2PublicApiService } from "../services/apiV2Service";
import logger from "../utils/logger";


/**
 * Custom hook to manage countries data with Redux caching
 * - Loads countries only once per session
 * - Caches data in Redux store
 * - Automatically cleared on logout
 */
export const useCountries = () => {
  const dispatch = useDispatch();
  const { countries, countriesLoading, countriesError } = useSelector(state => ({
    countries: state.dashboard?.countries || [],
    countriesLoading: state.dashboard?.countriesLoading || false,
    countriesError: state.dashboard?.countriesError || null,
  }));

  const loadCountries = async () => {
    // If countries are already loaded, don't fetch again
    if (countries.length > 0) {
      logger.log("gjfkkrjejrjr [useCountries] Countries already loaded:", countries.length);
      return countries;
    }

    // If already loading, don't start another request
    if (countriesLoading) {
      return countries;
    }

    try {
      dispatch(setCountriesLoading(true));
      dispatch(setCountriesError(null));

      logger.log("gjfkkrjejrjr [useCountries] Fetching countries from API...");
      const response = await v2PublicApiService.getCountries();
      logger.log("gjfkkrjejrjr [useCountries] API response:", response);

      // API returns: {"data": [...]}
      // After interceptor: {data: {"data": [...]}}
      // So we need: response.data.data
      if (response && response.data && Array.isArray(response.data.data)) {
        logger.log("gjfkkrjejrjr [useCountries] Dispatching countries to Redux:", response.data.data.length);
        dispatch(setCountries(response.data.data));
        return response.data.data;
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback: in case response structure is different
        logger.log("gjfkkrjejrjr [useCountries] Dispatching countries (fallback):", response.data.length);
        dispatch(setCountries(response.data));
        return response.data;
      } else {
        throw new Error("No countries data received");
      }
    } catch (error) {
      logger.error("gjfkkrjejrjr [useCountries] Error loading countries:", error);
      dispatch(setCountriesError(error.message || "Failed to load countries"));
      return [];
    }
  };

  // Auto-load countries when hook is first used
  useEffect(() => {
    if (countries.length === 0 && !countriesLoading && !countriesError) {
      loadCountries();
    }
  }, []);

  return {
    countries,
    countriesLoading,
    countriesError,
    loadCountries,
    hasCountries: countries.length > 0,
  };
};
